import axiosInstance from '../../core/axiosConfig.ts';
import * as Sentry from '@sentry/react';

export type CreditEntryType =
    | 'EARN' | 'TRIP_BONUS' | 'STREAK' | 'REFERRAL' | 'ADJUSTMENT'
    | 'RESTORE' | 'REDEEM' | 'EXPIRE' | 'CLAWBACK';

export type CreditLedgerRow = {
    id: number;
    customerId: number;
    customerName: string | null;
    customerEmail: string | null;
    type: CreditEntryType;
    /** Signed as stored: a redemption is negative, so the column sums to the balance. */
    amount: number;
    remaining: number;
    orderNumber: string | null;
    /** Where the order was placed; null for credit with no order behind it. */
    restaurantName: string | null;
    note: string | null;
    relatedCustomerName: string | null;
    createdAt: string;
    expiresAt: string | null;
};

export type CustomerCreditTotals = {
    customerId: number;
    customerName: string | null;
    customerEmail: string | null;
    phoneNumber: string | null;
    earned: number;
    spent: number;
    expired: number;
    balance: number;
    entryCount: number;
};

export type Restaurant = { id: number; name: string };

export type ReportFilters = {
    from?: string | null;
    to?: string | null;
    restaurantIds?: number[];
};

export type Paged<T> = { rows: T[]; total: number };

/** The page reads X-Total-Count, the same header the panel's other lists use. */
const totalFrom = (response: { headers: Record<string, unknown> }, fallback: number) => {
    const raw = response.headers['x-total-count'] ?? response.headers['X-Total-Count'];
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const filterParams = (filters: ReportFilters) => {
    const params = new URLSearchParams();
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    // Repeated rather than comma-joined: Spring binds a repeated parameter to a
    // List without any converter, and a restaurant name can contain a comma.
    (filters.restaurantIds ?? []).forEach((id) => params.append('restaurantIds', String(id)));
    return params;
};

export default class CreditReportService {
    async totals(filters: ReportFilters, start: number, end: number, sort: string, order: string): Promise<Paged<CustomerCreditTotals>> {
        try {
            const params = filterParams(filters);
            params.append('_start', String(start));
            params.append('_end', String(end));
            params.append('_sort', sort);
            params.append('_order', order);
            const response = await axiosInstance.get(`/admin/credit/totals?${params.toString()}`);
            return { rows: response.data, total: totalFrom(response, response.data.length) };
        } catch (e) {
            Sentry.captureException(e, { tags: { source: 'credit-totals' } });
            throw e;
        }
    }

    async ledger(filters: ReportFilters, start: number, end: number, type?: string | null): Promise<Paged<CreditLedgerRow>> {
        try {
            const params = filterParams(filters);
            params.append('_start', String(start));
            params.append('_end', String(end));
            params.append('_sort', 'createdAt');
            params.append('_order', 'DESC');
            if (type) params.append('type', type);
            const response = await axiosInstance.get(`/admin/credit/ledger?${params.toString()}`);
            return { rows: response.data, total: totalFrom(response, response.data.length) };
        } catch (e) {
            Sentry.captureException(e, { tags: { source: 'credit-ledger' } });
            throw e;
        }
    }

    /**
     * The restaurants offered in the filter. One page, sorted by name.
     *
     * <p>The language code is ENG, not EN - the enum is three letters, and
     * anything else is rejected by the endpoint rather than defaulted.
     *
     * <p>Throws rather than returning an empty list on failure. An empty
     * dropdown and a broken one look the same to whoever is trying to filter,
     * and the first version of this swallowed the error so completely that a
     * wrong language code went unnoticed until it turned up in a server log.
     */
    async restaurants(): Promise<Restaurant[]> {
        try {
            const response = await axiosInstance.get('/location/list?language=ENG&_start=0&_end=500&_sort=name&_order=ASC');
            return (response.data ?? []).map((row: { id: number; name: string }) => ({ id: row.id, name: row.name }));
        } catch (e) {
            Sentry.captureException(e, { tags: { source: 'credit-report-restaurants' } });
            throw e;
        }
    }
}
